import { faCalendar, faUser, faUserGroup, faCommentAlt, faAddressBook } from '@fortawesome/pro-light-svg-icons'
import { useState, useMemo, useCallback, Fragment } from 'react'
import { WhiteBox } from '../../../baseComponents/AppLayout/WhiteBox'
import { ButtonOrLoader } from '../../../baseComponents/Button'
import { ModalOpenedTy } from '../../../baseComponents/Modal/types'
import ModalAi from '../../../baseComponents/ModalsLayouts/ModalAi'
import TwinIcon, { TwinIconToolTip } from '../../../baseComponents/TwinIcon'
import { CustomRenderRoundedImage } from '../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import TwinToolTip from '../../../baseComponents/TwinToolTip'
import TwinTrans from '../../../baseComponents/TwinTrans'
import { displayDateWithFormattedHours } from '../../../utils/globals/date'
import { TwinDictionary } from '../../../utils/globals/dictionary'
import useIsLoading from '../../../utils/hooks/useIsLoading'
import useTwinTranslation from '../../../utils/hooks/useTwinTranslation'
import { DictionaryEmployee } from '../../../utils/reducers/company/employees'
import { getEmployees, getGroups } from '../../../utils/reducers/getters'
import { DictionaryGroup } from '../../../utils/reducers/groups/groups'
import { ModalAiResumeAndTaskFnc } from './types'
import { twinFetchPostJSON } from '../../../utils/globals/data'


interface ModalAIResumeAndTaskProps extends ModalAiResumeAndTaskFnc {
    data: TwinDictionary
    setOpenedAi: React.Dispatch<React.SetStateAction<ModalOpenedTy>>
    getData?: () => Promise<void>
}

const ModalAIResumeAndTask: React.FC<ModalAIResumeAndTaskProps> = ({ data, setOpenedAi, ...displayResponse }) => {
    const { response, setResponse } = useModalAIResumeAndTaskLogic()
    return (
        <ModalAi setOpened={setOpenedAi} url='/api/app/task/createResumeAndTaskIa' extraData={data} setResponse={setResponse} responseRendered={response ? <DisplayResponseAI response={response} setOpenedAi={setOpenedAi} data={data} {...displayResponse} /> : undefined} />
    )
}

const useModalAIResumeAndTaskLogic = () => {
    const [response, setResponse] = useState<null | TwinDictionary>(null)
    return { response, setResponse }
}

interface DisplayResponseAIProps extends DisplayResponseAILogicProps {
}

const DisplayResponseAI: React.FC<DisplayResponseAIProps> = ({ response, ...logic }) => {
    const { onSubmit, loading } = useDisplayResponseAILogic({ response, ...logic })
    const memoEmployees = useMemo(() => getEmployees() || {}, [])
    const memoGroups = useMemo(() => getGroups() || {}, [])
    const { t } = useTwinTranslation()
    const renderTask: JSX.Element[] = []
    for (const key in response.task) {
        const task = response.task[key]
        renderTask.push(<TaskRenderer employees={memoEmployees} groups={memoGroups} myKey={key} task={task} key={key} />)
    }
    return (
        <div className='justify-start text-left'>
            <h4 className='medium20'><TwinTrans transKey='hereIsYourResume'>Aquí tienes tu resumen</TwinTrans></h4>
            <span className='mt-10 block'>{response.resume}</span>
            {renderTask.length ?
                <div>
                    <h4 className='regular18 mt-20'><TwinTrans transKey='suggestedTasks'>Tareas sugeridas</TwinTrans></h4>
                    <div className='grid-cols-3 grid gap-10 mt-10'>
                        {renderTask}
                    </div>
                </div>
                : null
            }
            <div className='mt-20'>
                <ButtonOrLoader onClick={onSubmit} buttonIsDisabled={loading} textButton={t('create', 'Crear')} />
            </div>
        </div>
    )
}

interface DisplayResponseAILogicProps extends ModalAiResumeAndTaskFnc {
    response: TwinDictionary
    setOpenedAi: React.Dispatch<React.SetStateAction<ModalOpenedTy>>
    getData?: () => Promise<void>
    data: TwinDictionary
}

const useDisplayResponseAILogic = ({ response, setOpenedAi, data, createResumeFnc, getData }: DisplayResponseAILogicProps) => {
    const { loading, startLoading } = useIsLoading()
    const onSubmit = useCallback(async () => {
        startLoading()
        if (response.resume) {
            await createResumeFnc(data, response.resume)
        }
        if (response.task) {
            for (const element of response.task) {
                await twinFetchPostJSON('/api/app/task/createTask', {
                    TaskId: data.id,
                    CustomerId: data.CustomerId,
                    ...element
                })
            }
        }
        await getData?.()
        setOpenedAi(null)
    }, [response, setOpenedAi, getData, data, startLoading, createResumeFnc])

    return { onSubmit, loading }
}


interface TaskRendererProps {
    task: TwinDictionary
    myKey: string
    employees: DictionaryEmployee
    groups: DictionaryGroup
}

const TaskRenderer: React.FC<TaskRendererProps> = ({ task, myKey, employees, groups }) => {
    return (
        <WhiteBox className='flex flex-col gap-y-5 rounded-lg bg-white px-17 py-10'>
            <div className='flex gap-x-5 h-40'>
                <TaskFlowTaskName id={myKey} name={task.name} />
            </div>
            <div className='light12 text-gray-97 h-20'>
                {task.start_date ?
                    <Fragment>
                        <TwinIcon icon={faCalendar} className='mr-10' />
                        <span>{displayDateWithFormattedHours(new Date(task.start_date))}</span>
                    </Fragment>
                    : null
                }
            </div>
            <div className='flex gap-x-10 items-center justify-between'>
                <div className='flex items-center gap-x-10'>
                    {task.EmployeeId ? <CustomRenderRoundedImage className='h-27 w-27' name={employees?.[task.EmployeeId]?.fullname_short} icon={faUser} img={employees?.[task.EmployeeId]?.profile_image ?? '/user-png.png'} /> : null}
                    {task.GroupId ? <CustomRenderRoundedImage className='h-27 w-27' name={groups?.[task.GroupId]?.name} icon={faUserGroup} img={groups?.[task.GroupId]?.photo ?? '/group.png'} /> : null}
                </div>
                <div className='flex items-center gap-x-10'>
                    {task.description ? <TwinIconToolTip icon={faCommentAlt} classNameIcon='h-20 mt-4' message={task.description} classNameTooltip='max-w-300' id={'tooltipDescription' + myKey} /> : null}
                    {task.CustomerContactPersonId && task.contactPersonName ? <TwinIconToolTip icon={faAddressBook} classNameTooltip='max-w-300' classNameIcon='h-24 mt-4' message={task.contactPersonName} id={'tooltipContactPerson' + myKey} /> : null}
                </div>
            </div>
        </WhiteBox>
    )
}

interface TaskFlowTaskNameProps {
    id: string
    name?: string
}

const TaskFlowTaskName: React.FC<TaskFlowTaskNameProps> = ({ id, name }) => {
    const { t } = useTwinTranslation()
    const taskName = name || t('title', 'Título')
    const idTooltip = useMemo(() => 'Tooltip_TaskFlowTaskName' + String(id), [id])
    return (
        <div className='flex-auto w-1'>
            <TwinToolTip id={idTooltip} place='top' className='custom_render_task_name_tooltip max-w-300'>
                <p className='text-wrap-initial'>{taskName}</p>
            </TwinToolTip>
            <div data-tooltip-id={idTooltip} className={'cursor-pointer twin_two_lines_elipsis ' + (!name ? 'text-gray-97' : '')}>{taskName}</div>
        </div>
    )
}

export default ModalAIResumeAndTask